import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search as SearchIcon, X } from "lucide-react";
import { API } from "../App";

/**
 * Sticky-header search box with team autocomplete.
 * - Submits `q` to /search route on Enter or pick.
 * - Calls /api/search/teams as the user types (300ms debounce).
 */
export const SearchBar = ({ initialQuery = "", autoFocus = false }) => {
  const [q, setQ] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const blurTimerRef = useRef(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/search/teams`, {
          params: { q: q.trim(), limit: 8 },
        });
        setSuggestions(r.data?.results || []);
      } catch {
        setSuggestions([]);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const submit = (val) => {
    const term = (val || q).trim();
    if (!term) return;
    setOpen(false);
    setSuggestions([]);
    inputRef.current?.blur();
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  return (
    <div className="relative w-full" data-testid="header-search">
      <div className="relative flex items-center">
        <SearchIcon className="absolute left-3 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimerRef.current = setTimeout(() => setOpen(false), 150);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") inputRef.current?.blur();
          }}
          placeholder="Search team, league, country…"
          data-testid="search-input"
          className="w-full h-9 pl-9 pr-8 text-sm rounded-full bg-muted text-foreground placeholder:text-muted-foreground/70 border border-border focus:outline-none focus:ring-2 focus:ring-primary/40 focus:bg-background"
        />
        {q && (
          <button
            type="button"
            onClick={() => {
              setQ("");
              setSuggestions([]);
              inputRef.current?.focus();
            }}
            data-testid="search-clear"
            className="absolute right-2.5 text-muted-foreground hover:text-foreground"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div
          className="absolute top-full mt-1 left-0 right-0 z-50 rounded-xl border border-border bg-card shadow-lg overflow-hidden"
          data-testid="search-suggestions"
          onMouseDown={(e) => e.preventDefault()}
        >
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setQ(s);
                submit(s);
              }}
              data-testid={`search-suggestion-${s.toLowerCase().replace(/\s+/g, "-")}`}
              className="w-full text-left px-3.5 py-2 text-sm hover:bg-muted transition-colors flex items-center gap-2"
            >
              <SearchIcon className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="truncate">{s}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
